import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { AdminUser } from '../lib/authService'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Heart,
  MessageCircle,
  Clock,
  Globe,
  Lock,
  Shield,
  CheckCircle,
  X,
  Check,
  Plus,
  Loader2,
  AlertCircle,
} from 'lucide-react'

interface PrayerRequest {
  id: string
  user_id: string
  title: string
  description: string
  category: string
  privacy: string
  is_anonymous: boolean
  requester_name?: string
  approval_status: 'pending' | 'approved' | 'rejected'
  created_at: string
  profiles?: {
    id: string
    first_name: string
    last_name: string
    photo_url?: string
  }
}

interface PrayerRequestsProps {
  user: AdminUser | null
}

export default function PrayerRequests({ user: currentUser }: PrayerRequestsProps) {
  const [pendingRequests, setPendingRequests] = useState<PrayerRequest[]>([])
  const [approvedRequests, setApprovedRequests] = useState<PrayerRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'pending' | 'approved'>('pending')
  const [rejectionReason, setRejectionReason] = useState('')
  const [rejectingId, setRejectingId] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newPrayer, setNewPrayer] = useState({
    title: '',
    description: '',
    category: 'ministry' as string,
    privacy: 'church_only' as string
  })

  useEffect(() => {
    loadPrayerRequests()
  }, [currentUser])

  const loadPrayerRequests = async () => {
    if (!currentUser?.churchId) return

    try {
      setLoading(true)

      // Get pending requests with profile info
      const { data: pending, error: pendingError } = await supabase
        .from('prayers')
        .select(`
          *,
          profiles:user_id (
            id,
            first_name,
            last_name,
            photo_url
          )
        `)
        .eq('church_id', currentUser.churchId)
        .eq('approval_status', 'pending')
        .is('withdrawn_at', null)
        .order('created_at', { ascending: true })

      if (pendingError) throw pendingError

      // Get approved requests with profile info
      const { data: approved, error: approvedError } = await supabase
        .from('prayers')
        .select(`
          *,
          profiles:user_id (
            id,
            first_name,
            last_name,
            photo_url
          )
        `)
        .eq('church_id', currentUser.churchId)
        .eq('approval_status', 'approved')
        .is('withdrawn_at', null)
        .order('created_at', { ascending: false})
        .limit(20)

      if (approvedError) throw approvedError

      setPendingRequests(pending || [])
      setApprovedRequests(approved || [])
    } catch (error) {
      console.error('Error loading prayer requests:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async (requestId: string) => {
    try {
      const { error } = await supabase
        .from('prayers')
        .update({
          approval_status: 'approved',
          approved_by: currentUser?.id,
          approved_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId)

      if (error) throw error

      await loadPrayerRequests()
    } catch (error) {
      console.error('Error approving prayer request:', error)
      alert('Failed to approve prayer request')
    }
  }

  const handleReject = async (requestId: string) => {
    if (!rejectionReason.trim()) {
      alert('Please provide a reason for rejection')
      return
    }

    try {
      const { error } = await supabase
        .from('prayers')
        .update({
          approval_status: 'rejected',
          approved_by: currentUser?.id,
          rejection_reason: rejectionReason,
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId)

      if (error) throw error

      setRejectionReason('')
      setRejectingId(null)
      await loadPrayerRequests()
    } catch (error) {
      console.error('Error rejecting prayer request:', error)
      alert('Failed to reject prayer request')
    }
  }

  const handleWithdraw = async (requestId: string, requestTitle: string) => {
    if (!confirm(`Withdraw "${requestTitle}"?\n\nThis will hide the prayer request from public view but preserve it in church records.`)) {
      return
    }

    if (!currentUser?.id) return

    try {
      const { error } = await supabase
        .from('prayers')
        .update({
          withdrawn_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          approved_by: currentUser.id // Track who withdrew it
        })
        .eq('id', requestId)

      if (error) throw error
      await loadPrayerRequests()
    } catch (error) {
      console.error('Error withdrawing prayer request:', error)
      alert('Failed to withdraw prayer request')
    }
  }

  const handleCreateChurchPrayer = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!newPrayer.title.trim()) {
      alert('Please enter a prayer title')
      return
    }

    try {
      const { error } = await supabase
        .from('prayers')
        .insert({
          user_id: currentUser?.id,
          church_id: currentUser?.churchId,
          title: newPrayer.title.trim(),
          description: newPrayer.description.trim() || null,
          category: newPrayer.category,
          privacy: newPrayer.privacy,
          is_anonymous: false,
          approval_status: 'approved', // Admin prayers auto-approved
          approved_by: currentUser?.id,
          approved_at: new Date().toISOString(),
          status: 'active',
          prayer_count: 0
        })

      if (error) throw error

      alert('Church prayer created successfully!')
      setShowCreateModal(false)
      setNewPrayer({ title: '', description: '', category: 'ministry', privacy: 'church_only' })
      await loadPrayerRequests()
    } catch (error) {
      console.error('Error creating church prayer:', error)
      alert('Failed to create church prayer')
    }
  }

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      healing: 'Healing',
      guidance: 'Guidance',
      provision: 'Provision',
      relationship: 'Relationship',
      ministry: 'Ministry',
      other: 'Other'
    }
    return labels[category] || category
  }

  const getPrivacyIcon = (privacy: string) => {
    const icons = {
      public: <Globe className="h-3 w-3" />,
      church_only: <Shield className="h-3 w-3" />,
      private: <Lock className="h-3 w-3" />
    }
    return icons[privacy as keyof typeof icons] || <Lock className="h-3 w-3" />
  }

  const getPrivacyLabel = (privacy: string) => {
    const labels: Record<string, string> = {
      public: 'Public',
      church_only: 'Church Only',
      private: 'Private'
    }
    return labels[privacy] || privacy
  }

  const renderPrayerCard = (request: PrayerRequest) => {
    const profile = request.profiles
    const displayName = request.is_anonymous
      ? request.requester_name || 'Anonymous'
      : (profile?.first_name && profile?.last_name)
        ? `${profile.first_name} ${profile.last_name}`
        : profile?.first_name || 'A Member'

    const avatarUrl = request.is_anonymous ? null : profile?.photo_url

    return (
      <Card key={request.id} className="hover:shadow-md transition-shadow">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={displayName}
                  className="h-10 w-10 rounded-full object-cover"
                />
              ) : (
                <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                  <span className="font-semibold text-sm">
                    {displayName.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <div>
                <p className="font-semibold">{displayName}</p>
                <p className="text-sm text-muted-foreground">
                  {request.is_anonymous ? 'Anonymous Request' : 'Prayer Request'}
                </p>
              </div>
            </div>
            <Badge variant="secondary">
              {getCategoryLabel(request.category)}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold mb-2">{request.title}</h3>
            {request.description && (
              <p className="text-muted-foreground whitespace-pre-wrap">{request.description}</p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Badge variant="outline" className="flex items-center gap-1">
              {getPrivacyIcon(request.privacy)}
              <span>{getPrivacyLabel(request.privacy)}</span>
            </Badge>
          </div>

          <p className="text-sm text-muted-foreground flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {new Date(request.created_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </p>

          {request.approval_status === 'pending' && (
            <div className="space-y-3">
              {rejectingId === request.id ? (
                <div className="space-y-2">
                  <Textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Reason for rejection..."
                    rows={2}
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleReject(request.id)}
                      variant="destructive"
                      className="flex-1"
                    >
                      Confirm Reject
                    </Button>
                    <Button
                      onClick={() => {
                        setRejectingId(null)
                        setRejectionReason('')
                      }}
                      variant="outline"
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleApprove(request.id)}
                    className="flex-1"
                  >
                    <Check className="mr-2 h-4 w-4" />
                    Approve
                  </Button>
                  <Button
                    onClick={() => setRejectingId(request.id)}
                    variant="destructive"
                    className="flex-1"
                  >
                    <X className="mr-2 h-4 w-4" />
                    Reject
                  </Button>
                </div>
              )}
            </div>
          )}

          {request.approval_status === 'approved' && (
            <div className="flex justify-end">
              <Button
                onClick={() => handleWithdraw(request.id, request.title)}
                variant="outline"
                size="sm"
              >
                Withdraw
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Prayer Requests</h1>
          <p className="text-muted-foreground">
            Review and approve prayer requests from your church members
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create Church Prayer
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'pending' | 'approved')}>
        <TabsList>
          <TabsTrigger value="pending">
            Pending ({pendingRequests.length})
          </TabsTrigger>
          <TabsTrigger value="approved">
            Approved ({approvedRequests.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-4 mt-4">
          {pendingRequests.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <CheckCircle className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-lg font-medium">No pending prayer requests</p>
                <p className="text-sm text-muted-foreground">You're all caught up!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {pendingRequests.map(renderPrayerCard)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="approved" className="space-y-4 mt-4">
          {approvedRequests.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Heart className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-lg font-medium">No approved prayer requests yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {approvedRequests.map(renderPrayerCard)}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Church Prayer Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900">
          <DialogHeader>
            <DialogTitle>Create Church Prayer</DialogTitle>
            <DialogDescription>
              Create a prayer request on behalf of the church (auto-approved)
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateChurchPrayer}>
            <div className="space-y-4 py-4">
              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Prayer Title *</Label>
                <Input
                  id="title"
                  value={newPrayer.title}
                  onChange={(e) => setNewPrayer({ ...newPrayer, title: e.target.value })}
                  placeholder="Enter prayer title..."
                  required
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={newPrayer.description}
                  onChange={(e) => setNewPrayer({ ...newPrayer, description: e.target.value })}
                  placeholder="Provide details about this prayer need..."
                  rows={6}
                />
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select
                  value={newPrayer.category}
                  onValueChange={(value) => setNewPrayer({ ...newPrayer, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="healing">Healing</SelectItem>
                    <SelectItem value="guidance">Guidance</SelectItem>
                    <SelectItem value="provision">Provision</SelectItem>
                    <SelectItem value="relationship">Relationship</SelectItem>
                    <SelectItem value="ministry">Ministry</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Privacy */}
              <div className="space-y-2">
                <Label>Privacy</Label>
                <div className="space-y-2">
                  <Card
                    className={`cursor-pointer transition-colors ${newPrayer.privacy === 'church_only' ? 'border-primary' : ''}`}
                    onClick={() => setNewPrayer({ ...newPrayer, privacy: 'church_only' })}
                  >
                    <CardContent className="flex items-start p-4">
                      <input
                        type="radio"
                        value="church_only"
                        checked={newPrayer.privacy === 'church_only'}
                        onChange={() => setNewPrayer({ ...newPrayer, privacy: 'church_only' })}
                        className="mt-1"
                      />
                      <div className="ml-3">
                        <div className="font-medium flex items-center gap-2">
                          <Shield className="h-4 w-4" />
                          Church Only
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Visible only to your church members
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card
                    className={`cursor-pointer transition-colors ${newPrayer.privacy === 'public' ? 'border-primary' : ''}`}
                    onClick={() => setNewPrayer({ ...newPrayer, privacy: 'public' })}
                  >
                    <CardContent className="flex items-start p-4">
                      <input
                        type="radio"
                        value="public"
                        checked={newPrayer.privacy === 'public'}
                        onChange={() => setNewPrayer({ ...newPrayer, privacy: 'public' })}
                        className="mt-1"
                      />
                      <div className="ml-3">
                        <div className="font-medium flex items-center gap-2">
                          <Globe className="h-4 w-4" />
                          Public
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Can be seen by other churches using the app
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </Button>
              <Button type="submit">
                Create Prayer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
